import { searchDocuments } from '../rag_utils';
import { createOpenAIProxy } from './openaiProxy';

// Web Search Function - Uses REAL OpenAI web search via backend
export const performWebSearch = async (query, openaiParam, conversationMessages, selectedModel) => {
  try {
    console.log(`🌐 Performing REAL web search: ${query}`);

    // Call backend endpoint which uses OpenAI's web_search tool
    const response = await fetch('/api/web_search', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query, model: selectedModel })
    });

    if (!response.ok) {
      throw new Error(`Web search failed: ${response.status}`);
    }

    const data = await response.json();

    if (!data.success) {
      throw new Error(data.error || 'Web search failed');
    }

    console.log(`✅ Web search successful: ${data.content.length} characters`);

    // Extract citations from content (OpenAI includes them as [1], [2], etc.)
    const citations = [];
    const citationMatches = data.content.match(/\[(\d+)\]/g);
    if (citationMatches) {
      citationMatches.forEach(match => {
        const num = match.match(/\d+/)[0];
        citations.push({
          url: `#citation-${num}`,
          title: `Source ${num}`,
          content: `Web search citation ${num}`
        });
      });
    }

    return {
      success: true,
      content: data.content,
      citations: citations,
      source: 'Web Search (Real-time)',
      fallback: false
    };

  } catch (error) {
    console.error('Web search error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};

// RAG Search Function - Enhanced functionality
export const searchRAG = async (query, openaiClientParam, conversationMessages, selectedModel, buildRAGSystemPrompt) => {
  try {
    console.log(`📚 Starting RAG search for: ${query}`);
    console.log(`📚 Backend URL: /api/search`);

    // Use proxy for any OpenAI calls, but searchDocuments might not need OpenAI
    const openai = createOpenAIProxy();
    const searchResults = await searchDocuments(query, openai);
    console.log(`📚 Search results received:`, searchResults);

    if (searchResults && searchResults.documents && searchResults.documents[0] && searchResults.documents[0].length > 0) {
      const documents = searchResults.documents[0];
      const metadatas = searchResults.metadatas[0];
      const distances = searchResults.distances[0];

      // Authority level display names with emojis
      const authorityNames = {
        'legal_interpretations': { name: 'LEGAL INT', emoji: '⚖️' },
        'code_and_regulations': { name: 'CODE/REG', emoji: '�' },
        'revenue_rulings': { name: 'REV RUL', emoji: '📜' },
        'revenue_procedures': { name: 'REV PROC', emoji: '📄' },
        'private_letter_rulings': { name: 'PLR', emoji: '💌' },
        'secondary_authorities': { name: 'SECONDARY', emoji: '📚' },
        'state_tax_language': { name: 'STATE TAX', emoji: '🗺️' },
        'rules_for_reviewing': { name: 'RULES', emoji: '📋' },
        'other_authorities': { name: 'OTHER', emoji: '�' },
        // Legacy mappings
        'internal_revenue_code': { name: 'IRC', emoji: '🏛️' },
        'treasury_regulations': { name: 'TREAS', emoji: '📋' },
        'cases': { name: 'CASE', emoji: '⚖️' }
      };

      // Build context from top results
      let context = '';
      const citations = [];

      for (let i = 0; i < documents.length; i++) {
        const similarity = 1 - distances[i];
        if (similarity > 0.45) { // Tuned threshold: high enough to filter noise, low enough to capture Hopkins treatise
          const metadata = metadatas[i] || {};
          const authorityLevel = metadata.authority_level || 'other_authorities';
          const authorityInfo = authorityNames[authorityLevel] || { name: 'OTHER', emoji: '📚' };
          // Include source info so AI knows which document this is from
          const sourceName = metadata.filename || 'Unknown Document';
          context += `\n[Document ${i + 1} - Source: ${sourceName} (${authorityInfo.name})]:\n${documents[i]}\n`;
          citations.push({
            source: metadata.filename || 'Unknown',
            page: metadata.page_number || 'Unknown',
            similarity: similarity,
            content: documents[i],
            authority_level: authorityLevel,
            authority_name: authorityInfo.name,
            authority_emoji: authorityInfo.emoji,
            authority_rank: metadata.authority_rank || 7
          });
        }
      }

      if (context.trim()) {
        // Generate response using RAG context
        const response = await openai.chat.completions.create({
          model: selectedModel,
          messages: [
            {
              role: "system",
              content: buildRAGSystemPrompt ? buildRAGSystemPrompt() : "You are a legal-domain assistant. Be comprehensive, professional and follow the Lawyer's instructions. Also consult below sources in giving answer."
            },
            ...conversationMessages.slice(0, -1), // Include conversation history except the last message
            {
              role: "user",
              content: `Context from knowledge base:\n${context}\n\nQuestion: ${query}\n\nPlease answer based on the provided context.`
            }
          ],
          temperature: 1.0,
          top_p: 1.0,
          presence_penalty: 0,
          frequency_penalty: 0
        });

        const responseText = response.choices[0].message.content;
        console.log(`✅ RAG response generated: ${responseText.length} characters`);

        return {
          success: true,
          content: responseText,
          citations: citations,
          source: 'Knowledge Base'
        };
      }
    }

    if (!searchResults) {
      console.log(`❌ Knowledge base search returned null - backend may be down`);
      return {
        success: false,
        error: 'Knowledge base unavailable - backend may not be running'
      };
    } else if (!searchResults.documents || !searchResults.documents[0] || searchResults.documents[0].length === 0) {
      console.log(`⚠️ Knowledge base search returned empty results for query: ${query}`);
      return {
        success: false,
        error: 'No relevant documents found in knowledge base for this query'
      };
    } else {
      console.log(`⚠️ No high-quality matches found (similarity > 0.3)`);
      return {
        success: false,
        error: 'No high-quality matches found in knowledge base'
      };
    }
  } catch (error) {
    console.error('RAG search error:', error);
    return {
      success: false,
      error: error.message
    };
  }
};
